import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import Login from './components/auth/Login';
import Signup from './components/auth/Signup';
import Logout from './components/auth/Logout';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/RestPassword';
import VerifyEmail from './components/auth/VerifyEmail';
import SessionExpired from './components/auth/SessionExpired';
import UserDeactivation from './components/auth/UserDeactivation';
import BusinessProfile from './components/auth/BusinessProfile';
import BusinessSettings from './components/auth/BusinessSettings';
import AiUsagePage from './components/ai/AiUsagePage';
import BusinessHoursPage from './components/settings/BusinessHoursPage';
import DashboardLayout from './components/layout/DashboardLayout';
import DashboardPage from './pages/DashboardPage';
import PlaceholderPage from './pages/PlaceholderPage';
import BotManagement from './botmanagement/BotManagement';
import { NAV_ITEMS } from './components/layout/navItems';
import BotConfigurationPage from './botConfiguration/BotConfigurationPage';
import AppearanceBrandingPage from './appearanceBranding/AppearanceBrandingPage';
import KnowledgeBasePage from './knowledgeBase/KnowledgeBasePage';
import VisitorChatWidgetPage from './visitorChatWidget/VisitorChatWidgetPage';
import AgentWorkspacePage from './agentWorkspace/AgentWorkspacePage';
import LeadManagementPage from './leadManagement/LeadManagementPage';
import AnalyticsReportsPage from './analyticsReports/AnalyticsReportsPage';
import MarketingTeamPage from './marketingTeam/MarketingTeamPage';
import FrontendIntegrationPage from './integrationQA/FrontendIntegrationPage';
import PermissionDenied from './integrationQA/components/PermissionDenied';
import { useWorkspace } from './integrationQA/context/WorkspaceContext';
import NotConnected from './components/common/NotConnected';
import SettingsTabs from './components/layout/SettingsTabs';
import { isPageConnected } from './api/connection';

// Bot Management has its own role vocabulary; map the signed-in API role onto it.
const BOT_ROLE = { admin: 'business_admin', agent: 'support_agent', viewer: 'analyst_manager' };

// The Frontend Integration & QA pages are an engineering checklist, not a product feature, so they are not in
// the sidebar and the route only exists when the team turns dev tools on (REACT_APP_SHOW_DEV_TOOLS=true).
const SHOW_DEV_TOOLS = process.env.REACT_APP_SHOW_DEV_TOOLS === 'true';

function Protected({ children }) {
  const { isAuthenticated } = useWorkspace();
  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

/** Client-side gate for UX only — every API call is still authorised by the server. */
function RequirePermission({ permission, resource, children }) {
  const { can, roleLabel } = useWorkspace();
  if (!permission || can(permission)) return children;
  return <PermissionDenied required={permission} role={roleLabel} resource={resource || 'this page'} backTo="/dashboard" />;
}

function BotManagementRoute() {
  const { role } = useWorkspace();
  return <BotManagement role={BOT_ROLE[role] || 'analyst_manager'} />;
}

/** Backend mode: screens without a backend API yet show "Coming soon — not connected" (src/api/connection.js). */
function Connected({ route, title, settingsTab = false, children }) {
  if (isPageConnected(route)) return children;
  if (settingsTab) return <><SettingsTabs /><NotConnected title={title} compact /></>;
  return <NotConnected title={title} />;
}

function routeElement(item) {
  // Marketing & Team, Integrations decides per tab (Team users & roles is connected).
  if (item.to !== '/marketing' && !isPageConnected(item.to)) return <NotConnected title={item.label} />;
  switch (item.to) {
    case '/bot-management': return <BotManagementRoute />;
    case '/bot-configuration': return <BotConfigurationPage />;
    case '/knowledge-base': return <KnowledgeBasePage />;
    case '/appearance-branding': return <AppearanceBrandingPage />;
    case '/visitor-chat': return <VisitorChatWidgetPage />;
    case '/agent-workspace': return <AgentWorkspacePage />;
    case '/leads': return <LeadManagementPage />;
    case '/analytics': return <AnalyticsReportsPage />;
    case '/marketing': return <MarketingTeamPage key="marketing" />;
    default: return <PlaceholderPage title={item.label} />;
  }
}

function AppRoutes() {
  const { isAuthenticated } = useWorkspace();
  const navigate = useNavigate();

  // apiClient fires this when the server answers 401 SESSION_EXPIRED (token already cleared).
  useEffect(() => {
    // Several requests can fail at once on one page; only the first needs to redirect.
    const handleExpired = () => {
      if (window.location.pathname !== '/session-expired') navigate('/session-expired', { replace: true });
    };
    window.addEventListener('chatbizz_session_expired', handleExpired);
    return () => window.removeEventListener('chatbizz_session_expired', handleExpired);
  }, [navigate]);

  return <Routes>
    <Route path="/signup" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Signup />} />
    <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />} />
    <Route path="/verify-email" element={<VerifyEmail />} />
    <Route path="/forgot-password" element={<ForgotPassword />} />
    <Route path="/reset-password" element={<ResetPassword />} />
    <Route path="/session-expired" element={<SessionExpired />} />
    <Route path="/logout" element={<Logout />} />
    <Route path="/deactivate-account" element={<Protected><UserDeactivation /></Protected>} />
    <Route path="/response-style" element={<Navigate to="/bot-configuration?section=response-style" replace />} />
    <Route element={<Protected><DashboardLayout /></Protected>}>
      <Route index element={<Navigate to="/dashboard" replace />} />
      <Route path="/dashboard" element={<Connected route="/dashboard" title="Dashboard"><DashboardPage /></Connected>} />
      {NAV_ITEMS.filter((n) => n.to !== '/dashboard').map((n) =>
        <Route key={n.to} path={n.to === '/leads' ? 'leads/*' : n.to.replace(/^\//, '')}
          element={<RequirePermission permission={n.permission} resource={n.label}>{routeElement(n)}</RequirePermission>} />
      )}
      {/* Settings area: Workspace (/settings), Business profile and Business settings are tabs of one area. */}
      <Route path="business-profile" element={<RequirePermission permission="business:manage" resource="the business profile"><BusinessProfile /></RequirePermission>} />
      <Route path="business-hours" element={<RequirePermission permission="business:manage" resource="business hours"><Connected route="/business-hours" title="Business hours" settingsTab><BusinessHoursPage /></Connected></RequirePermission>} />
      <Route path="ai-usage" element={<RequirePermission permission="billing:read" resource="AI usage"><Connected route="/ai-usage" title="AI usage" settingsTab><AiUsagePage /></Connected></RequirePermission>} />
      <Route path="business-settings" element={<RequirePermission permission="business:manage" resource="business settings"><BusinessSettings /></RequirePermission>} />
      {/* Team and Integrations live inside the Marketing module; these URLs open the matching tab. `key` remounts the page when switching. */}
      <Route path="integrations" element={<RequirePermission permission="marketing:manage" resource="Integrations"><MarketingTeamPage key="integrations" initialSection="integrations" /></RequirePermission>} />
      <Route path="team-members" element={<RequirePermission permission="users:manage" resource="Team Members"><MarketingTeamPage key="team" initialSection="team" /></RequirePermission>} />
      {SHOW_DEV_TOOLS && <Route path="frontend-qa" element={<FrontendIntegrationPage />} />}
    </Route>
    <Route path="*" element={<Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />} />
  </Routes>;
}
export default function App() { return <AppRoutes />; }
